import classNames from 'clsx';
import React, { type FC, memo, useCallback, useRef, useState } from 'react';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import type { Kind } from 'reactApp/types/Tree';
import { Avatar } from 'reactApp/ui/Avatar/Avatar';
import { sendDatalistThumbDwh } from 'reactApp/ui/Datalist/DataList.helpers';
import type { IThumb } from 'reactApp/ui/DataListItem/DataListItem.helpers';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { getIconType } from 'reactApp/ui/FileIcon/getIconType';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { ImageCancelable } from 'reactApp/ui/ImageCancelable/ImageCancelable';

import styles from './PicVK.css';

export type PicProps = {
    isFolder: boolean;
    thumbUrl?: IThumb;
    id?: string | undefined;
    ext?: string;
    isVirus?: boolean;
    isLarge?: boolean;
    author?: { name?: string; email: string };
    kind?: Kind;
    isAutoDelete?: boolean;
    isAuthorView?: boolean;
};

export const PicVk: FC<PicProps> = memo(function Pic(props) {
    const { kind } = props;

    const iconType = getIconType({
        isFolder: props.isFolder,
        kind: props.isFolder ? kind : undefined,
        ext: props.isFolder ? undefined : props.ext,
        isVirus: props.isFolder ? false : props.isVirus,
        isAutoDelete: props.isFolder ? props.isAutoDelete : false,
    });

    const [isLoaded, setLoaded] = useState<boolean>(false);

    const { src, srcSet } = props.thumbUrl || {};

    const startTime = useRef(performance.now());

    const handleThumbLoad = useCallback(() => {
        setLoaded(true);

        const endTime = performance.now();

        sendDatalistThumbDwh('load', EViewMode.list, kind, Math.round(endTime - startTime.current));
    }, [kind]);

    const handleThumbError = useCallback(() => {
        const endTime = performance.now();

        sendDatalistThumbDwh('fail', EViewMode.list, kind, Math.round(endTime - startTime.current), src);
    }, [kind, src]);

    const { isAuthorView, author } = props;

    if (isAuthorView && author) {
        const { email, name } = author;
        return (
            <div className={styles.pic}>
                <Hint text={name || email}>
                    <Avatar size={26} email={email} name={name} />
                </Hint>
            </div>
        );
    }

    return (
        <div className={styles.pic}>
            {author && (
                <Hint text={author.name || author.email}>
                    <div className={styles.author}>
                        <Avatar size={16} email={author.email} name={author.name} />
                    </div>
                </Hint>
            )}
            <div className={styles.container}>
                {!isLoaded && (
                    <div className={classNames({ [styles.icon]: true, [styles.icon_large]: props.isLarge })}>
                        <FileIcon size={props.isLarge ? 32 : 22} type={iconType} />
                    </div>
                )}
                {!props.isFolder && props.thumbUrl?.src && (
                    <ImageCancelable
                        className={classNames({
                            [styles.thumb]: true,
                            [styles.thumb_loaded]: isLoaded,
                        })}
                        src={src}
                        srcSet={srcSet ? `${srcSet} 2x` : ''}
                        alt=""
                        onError={handleThumbError}
                        onLoad={handleThumbLoad}
                    />
                )}
            </div>
        </div>
    );
});
